import {
  AnalyticsIcon,
  ClockIcon,
  CustomizeIcon,
  DeleteIcon,
  EditIcon,
  GearIcon,
  LockIcon,
  QrCodeIcon,
  WebsiteUrlIcon,
} from '@app/assets';
import { Button, Checkbox, Container, IconButton } from '@app/components';
import { Divider } from '@mui/material';
import { QRCodeOutDtoOverridden } from '../qr-code.types';
import { renderFriendlyQRCodeTypes } from '@app/utils/render-friendly-text.utils';
import { format } from 'date-fns';
import { FORMAT } from '@app/constants/formats';
import { ConfirmDialog } from '@app/hoc/confirm-dialog/ConfirmDialog';
import { useCallback } from 'react';
import NiceModal from '@ebay/nice-modal-react';
import { ChangeQRCodeNameModal } from './modals/ChangeQRCodeNameModal';
import { extractStylePropsFromQRDesign, getQRfileNameForDownload } from '../qr-code.utils';
import { ECodeType } from '@app/swagger-types';
import { clsxm } from '@app/styles/clsxm';
import { useTranslation } from 'react-i18next';
import { DownloadQRCodeModal } from './modals/DownloadQRCodeModal';
import { TypedLink } from '@app/router';
import { Routes } from '@app/constants/routes';
import { ANALYTICS_QUERY_PARAM_QR_IDS } from '@app/domain/analytics/analytics.url.hooks';
import { QRCodeRendererSVG, QRRendererProps } from './QRCodeRendererSVG';
import { PermissionCrown } from '@app/domain/plan/components/permission-crown/PermissionCrown';
import { usePlanLimits } from '@app/domain/plan/hooks/usePlanLimits';
import { EQRCodeStep, ESettingsTab } from '../hooks/useQRCodeNavigation';
import { typedEnv } from '@app/environment/typed-env';
import { useAuthorizedUser } from '@app/auth/useAuthorizedUser';

// TODO after release
// import { LabelIcon } from '@app/assets';

export const QRCodeContainer: React.FC<{
  qrCode: QRCodeOutDtoOverridden;
  handleDeleteQRCode?: (id: string) => void;
  handleSelectQRCode?: (qrCode: QRCodeOutDtoOverridden, selected: boolean) => void;
  handleNavigate?: (qrCodeId: string, action: EQRCodeStep, tab?: ESettingsTab) => void;
  getNavigationUrl?: (qrCodeId: string, action: EQRCodeStep, tab?: ESettingsTab) => string;
  selected?: boolean;
  className?: string;
  isPreviewMode?: boolean;
}> = ({
  qrCode,
  handleDeleteQRCode,
  handleSelectQRCode,
  getNavigationUrl,
  handleNavigate,
  selected = false,
  className,
  isPreviewMode,
}) => {
  const { t } = useTranslation();

  const handleSelectCheckbox = useCallback(() => {
    if (!handleSelectQRCode) {
      return;
    }
    handleSelectQRCode(qrCode, !selected);
  }, [handleSelectQRCode, qrCode, selected]);

  const isSelectable = Boolean(handleSelectQRCode);
  const { canEditQrInput } = usePlanLimits();
  const { is5GenUser } = useAuthorizedUser();

  const rendererProps: QRRendererProps = {
    ...qrCode,
    qrId: qrCode.id,
    ...(qrCode.design ? extractStylePropsFromQRDesign(qrCode.design) : undefined),
    publicLogoType: qrCode.publicLogoType,
    fileName: getQRfileNameForDownload(qrCode.id, qrCode.name),
    gtin: qrCode?.gs1?.gtin,
    value: isPreviewMode ? typedEnv.REACT_APP_HOST_URL : undefined,
  };

  const handleCanEditQRInputClick = () => {
    if (handleNavigate) {
      handleNavigate(qrCode.id, EQRCodeStep.INPUT);
    }
  };

  return (
    <Container
      rounded
      className={clsxm(
        'testid-container-qr-code',
        'grid grid-cols-1 gap-3 p-0 lg:grid-cols-[1fr_2px_auto] lg:gap-0',
        className
      )}
    >
      <div className="flex gap-2 p-4 lg:items-center" data-testid="qr-code-container">
        {isSelectable && (
          <div className="hidden lg:block">
            <Checkbox checked={selected} onChange={handleSelectCheckbox} />
          </div>
        )}
        {
          <div className="w-[100px]">
            <QRCodeRendererSVG {...rendererProps} />
          </div>
        }
        <div className="flex flex-1 flex-col gap-2 text-xs text-gray-500">
          <div className="flex justify-between gap-4">
            <div>
              <div className="text-xxs text-primary">{renderFriendlyQRCodeTypes(qrCode.codeType)}</div>
              <div className="mt-1 flex items-center gap-1 font-bold text-gray-800">
                <span className="testid-qr-code-name">{qrCode.name}</span>
                {qrCode.isPasswordProtected && (
                  <div className="bg-gray-200 rounded-lg p-1">
                    <LockIcon className="text-accept-dark-blue" />
                  </div>
                )}
                {!isPreviewMode && (
                  <IconButton
                    onClick={() => {
                      NiceModal.show(ChangeQRCodeNameModal, { name: qrCode.name, qrCodeId: qrCode.id });
                    }}
                    size="small"
                    className="text-gray-500 hover:text-gray-800"
                    variant="transparent"
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </div>
            </div>
            {isSelectable && (
              <div className="lg:hidden">
                <Checkbox checked={selected} onChange={handleSelectCheckbox} />
              </div>
            )}
          </div>
          {qrCode.codeType === ECodeType.WEBSITE ? (
            <div className="flex items-center gap-2 overflow-hidden">
              <div className="flex items-center gap-2">
                <WebsiteUrlIcon className="h-4 min-h-[16px] w-4 min-w-[16px] text-gray-700" />
                <span className="overflow-hidden" style={{ wordBreak: 'break-word' }}>
                  {qrCode.website?.url || ''}
                </span>
              </div>
            </div>
          ) : null}
          {qrCode.codeType === ECodeType.GS1 ? (
            <div className="flex items-center gap-2 overflow-hidden">
              <div className="flex items-center gap-2">
                <QrCodeIcon className="h-4 min-h-[16px] w-4 min-w-[16px] text-gray-700" />
                <span className="overflow-hidden" style={{ wordBreak: 'break-word' }}>
                  {`(01) ${qrCode.gs1?.gtin || ''}`}
                </span>
              </div>
            </div>
          ) : null}

          {qrCode.createdDate && (
            <div className="flex items-center gap-2">
              <ClockIcon className="min-h-4 min-w-4 h-4 w-4 text-gray-700" />
              {format(new Date(qrCode.createdDate), FORMAT.DATE_YEAR)}
            </div>
          )}
        </div>
      </div>
      <Divider flexItem className="lg:hidden" />
      {!isPreviewMode && (
        <>
          <Divider flexItem orientation="vertical" className="hidden py-4 lg:block" />
          <div className="flex flex-col justify-between gap-3 pb-4 lg:py-4">
            <div className="flex flex-1 items-center justify-between gap-4 px-3 lg:px-12">
              <div className="flex items-center rounded-[10px] bg-gray-100 px-3 py-2 text-xxs text-gray-500">
                {t('qr.scans')}
                <span className="ml-4 text-m font-bold text-gray-800">{qrCode.amountOfScans || 0}</span>
              </div>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  NiceModal.show(DownloadQRCodeModal, { rendererProps });
                }}
              >
                {t('qr.buttonDownload')}
              </Button>
            </div>
            <Divider flexItem />
            <div className="flex flex-1 items-center justify-between gap-4 px-3 self-center lg:px-12">
              <div className="flex items-center gap-4 sm:gap-6">
                <a
                  href={getNavigationUrl && getNavigationUrl(qrCode.id, EQRCodeStep.INPUT)}
                  onClick={(e) => e.preventDefault()}
                >
                  <IconButton size="small" onClick={handleCanEditQRInputClick} variant="transparent">
                    <div className="relative">
                      <EditIcon />
                      {!is5GenUser && (
                        <PermissionCrown
                          className="text-base absolute -top-4 left-1/2 transform -translate-x-1/2"
                          requireUpgrade={!canEditQrInput}
                        />
                      )}
                    </div>
                  </IconButton>
                </a>
                <a
                  href={getNavigationUrl && getNavigationUrl(qrCode.id, EQRCodeStep.CUSTOMIZE)}
                  onClick={(e) => e.preventDefault()}
                >
                  <IconButton
                    size="small"
                    onClick={handleNavigate && (() => handleNavigate(qrCode.id, EQRCodeStep.CUSTOMIZE))}
                    variant="transparent"
                  >
                    <CustomizeIcon />
                  </IconButton>
                </a>
                <a
                  href={getNavigationUrl && getNavigationUrl(qrCode.id, EQRCodeStep.INPUT, ESettingsTab.CURRENT)}
                  onClick={(e) => e.preventDefault()}
                >
                  <IconButton
                    size="small"
                    onClick={
                      handleNavigate && (() => handleNavigate(qrCode.id, EQRCodeStep.INPUT, ESettingsTab.CURRENT))
                    }
                    variant="transparent"
                  >
                    <GearIcon />
                  </IconButton>
                </a>
                <TypedLink
                  to={Routes.customer.analytics.index}
                  queryParams={{ [ANALYTICS_QUERY_PARAM_QR_IDS]: qrCode.id }}
                >
                  <IconButton size="small" variant="transparent">
                    <AnalyticsIcon />
                  </IconButton>
                </TypedLink>
                {/* TODO implement after release */}
                {/* <IconButton size="small" disabled variant="transparent">
              <LabelIcon />
            </IconButton> */}
                <ConfirmDialog
                  trigger={
                    <IconButton size="small" variant="transparent">
                      <DeleteIcon />
                    </IconButton>
                  }
                  onConfirm={() => {
                    if (handleDeleteQRCode) {
                      handleDeleteQRCode(qrCode.id);
                    }
                  }}
                  containerClassName="max-w-[350px] text-center"
                  title={`${t('dialog.deleteQuestionTitle')} ${t('qr.entityName')}?`}
                  text={`${t('dialog.areYouSureToDeleteThis')} ${t('qr.entityName')}?"`}
                  confirmText={t('dialog.yesDelete')}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </Container>
  );
};
